import React from "react";
import _ from "underscore";

type TResizableComponentProps = {
    onResizeStart: (e: React.MouseEvent | React.TouchEvent) => void;
    setResizeEndCallback: (cb: (e: MouseEvent) => void) => void;
};

export type TResizableComponentState = {
    isResizing: boolean;
    startPosition: React.MouseEvent | React.TouchEvent | null;
    lastMousePosition: React.MouseEvent | React.TouchEvent | null;
};

export type TResizableComponentStateAndProps = TResizableComponentProps &
    Partial<TResizableComponentState>;

export default {
    wrap(
        /**
         * The component to wrap. It is handed the resize props and state --
         * TResizableComponentStateAndProps -- on top of whatever its own caller
         * passes through, but those pass-through props are not visible here, so
         * this cannot say more than that it is something React can render.
         */
        Component: React.ComponentType<any> | React.ForwardRefExoticComponent<any>
    ) {
        // Whatever the caller passes is spread into Component untouched. Naming
        // that here would need the wrapped component's own props, and
        // withAppContext has already erased those, so the call sites stay
        // unchecked until it goes.
        return class extends React.Component<any> {
            static displayName = Component.displayName;

            private _resizeEndCallback: ((e: MouseEvent) => void) | undefined;

            state = {
                isResizing: false,
                startPosition: null,
                lastMousePosition: null,
            };

            onResizeStart = (evt: React.MouseEvent | React.TouchEvent) => {
                document.addEventListener(_.getEndEvent(evt), this.onResizeEnd);
                document.addEventListener(_.getMoveEvent(evt), this.onResizeMove);

                evt.stopPropagation();

                this.setState({
                    startPosition: _.clone(evt),
                    isResizing: true,
                    lastMousePosition: _.clone(evt),
                });
            };

            onResizeMove = (evt) => {
                if (!this.state.isResizing) {
                    return;
                }

                this.setState({
                    lastMousePosition: _.clone(evt),
                });
            };

            setResizeEndCallback = (callback: (e: MouseEvent) => void) => {
                this._resizeEndCallback = callback;
            };

            onResizeEnd = (evt) => {
                if (!this.state.isResizing) {
                    return;
                }
                document.removeEventListener(_.getEndEvent(evt), this.onResizeEnd);
                document.removeEventListener(_.getMoveEvent(evt), this.onResizeMove);

                this.setState({ isResizing: false });

                if (this._resizeEndCallback) {
                    this._resizeEndCallback(evt);
                }
            };

            render() {
                return (
                    <Component
                        {...this.props}
                        {...this.state}
                        onResizeStart={this.onResizeStart}
                        setResizeEndCallback={this.setResizeEndCallback}
                    />
                );
            }
        };
    },
};
